import { buildSpecRows } from './vmkQuoteTable';

/** A4 page height (px) – matches quotePdf.css / pdfPageExport */
const PAGE_HEIGHT = 1123;

/* ── Legacy (non-branded) quote-pdf-page-inner chrome ── */
const LEGACY_PAGE_PADDING_Y = 90;
const LEGACY_PAGE_FOOTER_HEIGHT = 22;
const LEGACY_HEADER_HEIGHT_FIRST = 168;
const LEGACY_HEADER_HEIGHT = 112;
const TOTALS_LEGAL_HEIGHT = 268;
const LEGAL_FOOTER_HEIGHT = 196;

/* ── Branded template drawing page (vmk-drawing-page) ── */
/** .vmk-proposal-header: padding 32+18 + logo 40 */
const BRANDED_HEADER_HEIGHT = 90;
/** .vmk-template-footer contact bar */
const BRANDED_FOOTER_HEIGHT = 40;
/** .vmk-drawing-main padding 12 + 16 */
const BRANDED_MAIN_PADDING_Y = 28;
/** Offerte/Referentie box + margins on every branded drawing page */
const BRANDED_OFFER_BLOCK_HEIGHT = 52;
/** Optional section title block on first page of a named section */
const BRANDED_SECTION_HEADER_HEIGHT = 44;
/** VmkQuoteTotals on last drawing page of a section (~3–4 rows + mt-3) */
const BRANDED_TOTALS_HEIGHT = 130;
/**
 * Extra slack so borderline chunks push the next drawing to a new page
 * instead of clipping under overflow:hidden + teal footer.
 */
const BRANDED_PACK_SLACK = 64;
const LEGACY_PACK_SLACK = 36;

/* ── Per line-item estimates (match quotePdf.css table/drawing) ── */
const ROW_HEIGHT = 28;
const ROW_HEIGHT_SUBLINE = 40;
const PRICE_ROW_HEIGHT = 30;
const DRAWING_WRAP_PAD = 16;
/** Double rule between items: borders + margins (non-compact / compact) */
const ITEM_DIVIDER_HEIGHT = 25;
const ITEM_DIVIDER_HEIGHT_COMPACT = 17;

function estimateWrappedHeight(text, baseHeight, charsPerLine = 40, linePx = 14) {
  if (!text) return baseHeight;
  const lines = Math.max(1, Math.ceil(String(text).length / charsPerLine));
  if (lines <= 1) return baseHeight;
  return baseHeight + (lines - 1) * linePx;
}

function getContentLimit({
  isFirstPage,
  isLastPage,
  includePricing = true,
  reserveTailSpace = false,
  tailReserveHeight = null,
  useBrandedLayout = false,
  hasSectionHeader = false,
}) {
  if (useBrandedLayout) {
    const sectionReserve =
      isFirstPage && hasSectionHeader ? BRANDED_SECTION_HEADER_HEIGHT : 0;
    const footerReserve =
      isLastPage && reserveTailSpace
        ? tailReserveHeight ?? BRANDED_TOTALS_HEIGHT
        : 0;
    return (
      PAGE_HEIGHT -
      BRANDED_HEADER_HEIGHT -
      BRANDED_FOOTER_HEIGHT -
      BRANDED_MAIN_PADDING_Y -
      BRANDED_OFFER_BLOCK_HEIGHT -
      sectionReserve -
      footerReserve -
      BRANDED_PACK_SLACK
    );
  }

  const header = isFirstPage ? LEGACY_HEADER_HEIGHT_FIRST : LEGACY_HEADER_HEIGHT;
  const footerReserve =
    isLastPage && reserveTailSpace
      ? tailReserveHeight ??
        (includePricing ? TOTALS_LEGAL_HEIGHT : LEGAL_FOOTER_HEIGHT)
      : 0;
  return (
    PAGE_HEIGHT -
    LEGACY_PAGE_PADDING_Y -
    header -
    LEGACY_PAGE_FOOTER_HEIGHT -
    footerReserve -
    LEGACY_PACK_SLACK
  );
}

function estimateTableHeight(
  config,
  includePricing = true,
  quoteType,
  includeNijBegun = false
) {
  const specRows = buildSpecRows(config || {}, quoteType);
  let height = ROW_HEIGHT; // quantity / letter row
  height += ROW_HEIGHT; // profile / index row
  specRows.forEach((row) => {
    if (row?.subline) {
      height += estimateWrappedHeight(row.subline, ROW_HEIGHT_SUBLINE, 36, 12);
    } else {
      height += estimateWrappedHeight(row?.description, ROW_HEIGHT, 42, 14);
    }
  });
  if (includeNijBegun) {
    height += ROW_HEIGHT;
  }
  if (includePricing) {
    height += PRICE_ROW_HEIGHT;
  }
  return height + 12;
}

function estimateDrawingHeight(config, compact) {
  // CSS: max-height 420 (normal) / 200 (compact) + wrap padding-bottom 16
  const maxH = compact ? 200 : 420;
  const minH = compact ? 110 : 150;
  const width = config?.width || 1000;
  const height = config?.height || 1000;
  const colWidth = 250; // ~36% of branded content width
  const scaled = (height / Math.max(1, width)) * colWidth;
  return Math.min(maxH, Math.max(minH, scaled)) + DRAWING_WRAP_PAD;
}

/** Estimated pixel height of one line item on a pricing page */
export function estimateQuoteItemHeight(
  quote,
  {
    compact = false,
    includePricing = true,
    includeNijBegun = false,
  } = {}
) {
  const config = quote?.config || {};
  const tableHeight = estimateTableHeight(
    config,
    includePricing,
    quote?.type,
    includeNijBegun
  );
  const drawingHeight = estimateDrawingHeight(config, compact);
  return Math.ceil(Math.max(tableHeight, drawingHeight));
}

function estimateChunkHeight(
  quotes,
  compact,
  includePricing = true,
  includeNijBegun = false
) {
  const divider = compact ? ITEM_DIVIDER_HEIGHT_COMPACT : ITEM_DIVIDER_HEIGHT;
  return quotes.reduce((sum, quote, index) => {
    const gap = index > 0 ? divider : 0;
    return (
      sum +
      estimateQuoteItemHeight(quote, {
        compact,
        includePricing,
        includeNijBegun,
      }) +
      gap
    );
  }, 0);
}

/**
 * Pack saved quotes onto A4 pricing pages.
 * Fits as many whole items per page as estimates allow (typically 1–2).
 * Reserves space on the last page for totals when requested.
 *
 * Branded template pages use different chrome than legacy quote-pdf-page-inner;
 * underestimating that chrome (or item height) clips drawings under the footer.
 */
export function packQuotesIntoPages(
  quotes,
  {
    includePricing = true,
    reserveTailSpace = false,
    tailReserveHeight = null,
    useBrandedLayout = false,
    includeNijBegun = false,
    sectionName = null,
  } = {}
) {
  if (!quotes?.length) return [];

  const hasSectionHeader = Boolean(sectionName && String(sectionName).trim());
  const chunks = [];
  let index = 0;

  while (index < quotes.length) {
    const isFirstPage = chunks.length === 0;
    const chunk = [];

    while (index < quotes.length) {
      const trySize = chunk.length + 1;
      const isLastChunk = index + trySize >= quotes.length;
      const limit = getContentLimit({
        isFirstPage,
        isLastPage: isLastChunk,
        includePricing,
        reserveTailSpace,
        tailReserveHeight,
        useBrandedLayout,
        hasSectionHeader,
      });

      const candidate = [...chunk, quotes[index]];
      const compact = candidate.length >= 2;
      const candidateHeight = estimateChunkHeight(
        candidate,
        compact,
        includePricing,
        includeNijBegun
      );

      // Never start a block that won't fit with items already on the page —
      // move the whole drawing+table to the next page instead of clipping.
      if (chunk.length > 0 && candidateHeight > limit) {
        break;
      }

      chunk.push(quotes[index]);
      index += 1;

      if (index >= quotes.length) {
        break;
      }

      const nextTrySize = chunk.length + 1;
      const nextIsLastChunk = index + nextTrySize >= quotes.length;
      const nextLimit = getContentLimit({
        isFirstPage,
        isLastPage: nextIsLastChunk,
        includePricing,
        reserveTailSpace,
        tailReserveHeight,
        useBrandedLayout,
        hasSectionHeader,
      });
      const nextCandidate = [...chunk, quotes[index]];
      const nextCompact = nextCandidate.length >= 2;
      const nextHeight = estimateChunkHeight(
        nextCandidate,
        nextCompact,
        includePricing,
        includeNijBegun
      );

      if (nextHeight > nextLimit) {
        break;
      }
    }

    chunks.push(chunk);
  }

  return chunks;
}
